import React, { useMemo, useState } from 'react';
import { Pagination, Table } from 'react-bootstrap';

const ITENS_POR_PAGINA = 15;

const formatarMoeda = (valor) => {
  if (valor === null || valor === undefined) return '—';
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const GridSaldos = ({ dados = [], nomesMoedas = {} }) => {
  const [pagina, setPagina] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ coluna: 'apelido_conta', asc: true });

  const dadosOrdenados = useMemo(() => {
    const copia = [...dados];
    copia.sort((a, b) => {
      const va = a[ordenacao.coluna];
      const vb = b[ordenacao.coluna];
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === 'string') return ordenacao.asc ? va.localeCompare(vb) : vb.localeCompare(va);
      return ordenacao.asc ? va - vb : vb - va;
    });
    return copia;
  }, [dados, ordenacao]);

  const totalPaginas = Math.max(1, Math.ceil(dadosOrdenados.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
  const itensPagina = dadosOrdenados.slice(inicio, inicio + ITENS_POR_PAGINA);

  const alternarOrdenacao = (coluna) => {
    setOrdenacao((prev) =>
      prev.coluna === coluna ? { coluna, asc: !prev.asc } : { coluna, asc: true }
    );
    setPagina(1);
  };

  const renderSeta = (coluna) => {
    if (ordenacao.coluna !== coluna) return ' ↕';
    return ordenacao.asc ? ' ↑' : ' ↓';
  };

  const handleMudarPagina = (nova) => {
    if (nova < 1 || nova > totalPaginas) return;
    setPagina(nova);
  };

  const numeroPaginas = [];
  const maxVisivel = 5;
  let inicio_pag = Math.max(1, paginaAtual - Math.floor(maxVisivel / 2));
  const fim_pag = Math.min(totalPaginas, inicio_pag + maxVisivel - 1);
  if (fim_pag - inicio_pag + 1 < maxVisivel) inicio_pag = Math.max(1, fim_pag - maxVisivel + 1);
  for (let i = inicio_pag; i <= fim_pag; i += 1) numeroPaginas.push(i);

  if (dados.length === 0) {
    return <p className="text-muted mt-3">Nenhum saldo encontrado para os filtros informados.</p>;
  }

  return (
    <div className="mt-3">
      <Table striped bordered hover size="sm" responsive>
        <thead className="thead-dark">
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => alternarOrdenacao('apelido_conta')}>
              Conta{renderSeta('apelido_conta')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => alternarOrdenacao('tipo')}>
              Tipo{renderSeta('tipo')}
            </th>
            <th
              className="text-right"
              style={{ cursor: 'pointer' }}
              onClick={() => alternarOrdenacao('saldo_original')}
            >
              Saldo Original{renderSeta('saldo_original')}
            </th>
            <th className="text-center">Moeda</th>
            <th
              className="text-right"
              style={{ cursor: 'pointer' }}
              onClick={() => alternarOrdenacao('saldo_convertido')}
            >
              Saldo Convertido{renderSeta('saldo_convertido')}
            </th>
          </tr>
        </thead>
        <tbody>
          {itensPagina.map((item, index) => (
            <tr key={`${item.codigo_conta}-${item.codigo_ativo_original}-${index}`}>
              <td>{item.apelido_conta}</td>
              <td>{item.tipo}</td>
              <td className="text-right">{formatarMoeda(item.saldo_original)}</td>
              <td className="text-center">
                {nomesMoedas[item.codigo_ativo_original] || item.codigo_ativo_original}
              </td>
              <td className="text-right">{formatarMoeda(item.saldo_convertido)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center flex-wrap mt-2">
        <small className="text-muted">
          Mostrando {inicio + 1} até {Math.min(inicio + ITENS_POR_PAGINA, dadosOrdenados.length)} de{' '}
          {dadosOrdenados.length} registros
        </small>
        {totalPaginas > 1 && (
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => handleMudarPagina(1)} disabled={paginaAtual === 1} />
            <Pagination.Prev onClick={() => handleMudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1} />
            {inicio_pag > 1 && <Pagination.Ellipsis key="start-ellipsis" />}
            {numeroPaginas.map((n) => (
              <Pagination.Item key={n} active={n === paginaAtual} onClick={() => handleMudarPagina(n)}>
                {n}
              </Pagination.Item>
            ))}
            {fim_pag < totalPaginas && <Pagination.Ellipsis key="end-ellipsis" />}
            <Pagination.Next onClick={() => handleMudarPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} />
            <Pagination.Last onClick={() => handleMudarPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} />
          </Pagination>
        )}
      </div>
    </div>
  );
};

export default GridSaldos;
